import Icon from "@react-native-vector-icons/material-design-icons/static";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text, TextInput } from "react-native-paper";
import { Avatar } from "../../components/Avatar";
import TooltipWrapper from "../../components/TooltipWrapper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { useOnboarding } from "./OnboardingProvider";
import { ChangeLabel } from "./components/ChangeLabel";
import { OnboardingBox } from "./components/OnboardingBox";
import { PROFILE_SAVE_DEBOUNCE_MS } from "./constants";
import { sanitizeProfile } from "./helpers";
import type { OnboardingProfile } from "./types";

type Props = {
	title: string;
	description: string | undefined;
};

export const OnboardingProfileScreen = ({ title, description }: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { selectedProfile, onUpdateProfile, errors } = useOnboarding();

	const [draftProfile, setDraftProfile] = useState<OnboardingProfile>({
		username: selectedProfile?.username ?? "",
		firstName: selectedProfile?.firstName ?? "",
		lastName: selectedProfile?.lastName ?? "",
	});

	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout> | undefined;

		if (
			draftProfile.username !== selectedProfile?.username ||
			draftProfile.firstName !== selectedProfile?.firstName ||
			draftProfile.lastName !== selectedProfile?.lastName
		) {
			timeout = setTimeout(() => {
				onUpdateProfile(draftProfile);
			}, PROFILE_SAVE_DEBOUNCE_MS);
		}

		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [selectedProfile, draftProfile, onUpdateProfile]);

	return (
		<Box gap={10}>
			<OnboardingBox>
				<Box gap={14}>
					<Text variant="titleLarge">{title}</Text>
					{description ? (
						<Text
							variant="bodyMedium"
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{description}
						</Text>
					) : null}
				</Box>
				<Box gap={16}>
					<Box gap={4}>
						<TextInput
							mode="outlined"
							label={t("onboarding.steps.profile.usernameLabel")}
							value={draftProfile.username}
							onChangeText={(nextUsername) =>
								setDraftProfile(
									sanitizeProfile({
										...draftProfile,
										username: nextUsername,
									}),
								)
							}
							autoCapitalize="none"
							autoCorrect={false}
							autoComplete="username"
							textContentType="username"
							returnKeyType="next"
							autoFocus
							error={Boolean(errors?.profile)}
							style={{ backgroundColor: theme.colors.surface }}
						/>
						{errors?.profile ? (
							<Text
								variant="bodySmall"
								style={{
									color: theme.colors.error,
									paddingHorizontal: 16,
								}}
							>
								{errors.profile}
							</Text>
						) : null}
					</Box>
					<Box flexDirection="row" gap={12}>
						<TextInput
							mode="outlined"
							label={t("onboarding.steps.profile.firstNameLabel")}
							value={draftProfile.firstName}
							onChangeText={(nextFirstName) =>
								setDraftProfile({
									...draftProfile,
									firstName: nextFirstName,
								})
							}
							autoCapitalize="words"
							autoCorrect={false}
							autoComplete="given-name"
							textContentType="givenName"
							returnKeyType="next"
							style={{ flex: 1, backgroundColor: theme.colors.surface }}
						/>
						<TextInput
							mode="outlined"
							label={t("onboarding.steps.profile.lastNameLabel")}
							value={draftProfile.lastName}
							onChangeText={(nextLastName) =>
								setDraftProfile({
									...draftProfile,
									lastName: nextLastName,
								})
							}
							autoCapitalize="words"
							autoCorrect={false}
							autoComplete="family-name"
							textContentType="familyName"
							returnKeyType="done"
							style={{ flex: 1, backgroundColor: theme.colors.surface }}
						/>
					</Box>
					{selectedProfile?.username && !errors?.profile ? (
						<Box gap={6}>
							<TooltipWrapper
								title={t("onboarding.steps.profile.avatarTooltip")}
							>
								<Box flexDirection="row" alignItems="center" gap={2}>
									<Text variant="labelMedium">
										{t("onboarding.steps.profile.avatarLabel")}
									</Text>
									<Icon
										name="information-outline"
										size={14}
										color={theme.colors.onSurfaceVariant}
									/>
								</Box>
							</TooltipWrapper>
							<Avatar size={72} username={selectedProfile.username} />
						</Box>
					) : null}
				</Box>
			</OnboardingBox>
			<ChangeLabel />
		</Box>
	);
};
